import { useState, useEffect, useContext, useMemo } from 'react'
import PasswordStrengthBar from 'react-password-strength-bar'

import { useColorScheme } from '@mui/material/styles'
import { TextField, Button, CircularProgress, Typography, Divider, Link } from '@mui/material'

import GoogleIcon from '/assets/signin-icons/google.svg'
import AppleDarkIcon from '/assets/signin-icons/apple-dark.svg'
import AppleLightIcon from '/assets/signin-icons/apple-light.svg'
import LinkedInIcon from '/assets/signin-icons/linkedin.svg'

import signInWithGoogle from './google'
import signInWithApple from './apple'
import signInWithLinkedIn from './linkedin'

const providers = [
  { id: 'google', name: 'Google', signIn: signInWithGoogle },
  { id: 'apple', name: 'Apple', signIn: signInWithApple },
  { id: 'linkedin', name: 'LinkedIn', signIn: signInWithLinkedIn },
  { id: 'credentials', name: '' },
]

import { verifyChangePasswordRequest, acceptInviteAndLogin } from '/providers/auth-api'
import { AppContext } from '/providers/app-context'

const PasswordResetForm = ({ token }) => {

  const { apiSource, applyLoginResponse } = useContext(AppContext)
  const { mode, systemMode } = useColorScheme()

  const [stage, setStage] = useState("verifying")
  const [password, setPassword] = useState("")
  const [passwordScore, setPasswordScore] = useState(0)
  const [passwordError, setPasswordError] = useState('')
  const [clientName, setClientName] = useState("")
  const [loading, setLoading] = useState(null) // provider id or null
  const [error, setError] = useState('')

  useEffect(() => {
    verifyToken()
  }, [token])

  const themeMode = useMemo(() => {
    if (mode === "system") return systemMode
    return mode
  }, [mode, systemMode])

  const verifyToken = async () => {
    const response = await verifyChangePasswordRequest({ apiSource, token })
    setStage(response.verified ? "form" : "rejected")
    setClientName(response.verified ? response.client_name : "")
  }

  const passwordValidationMessage = (value, { requireStrength = false, score } = {}) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Password is required.'
    }
    if (trimmed.length < 8) {
      return 'Password must be at least 8 characters.'
    }
    const effectiveScore = typeof score === 'number' ? score : passwordScore
    if (requireStrength && effectiveScore < 3) {
      return 'Choose a stronger password before continuing.'
    }
    return ''
  }

  const handlePasswordScoreChange = (score) => {
    setPasswordScore(score)
    if (passwordError) {
      setPasswordError(passwordValidationMessage(password, { requireStrength: true, score }))
    }
  }

  const handlePasswordInputChange = (nextValue) => {
    setPassword(nextValue)
    if (passwordError) {
      setPasswordError(passwordValidationMessage(nextValue, { requireStrength: true }))
    }
  }

  const validatePasswordField = (value = password, options = {}) => {
    const message = passwordValidationMessage(value, options)
    setPasswordError(message)
    return !message
  }
  
  const clearFieldErrors = () => {
    setPasswordError('')
  }

  const handleProviderRegistration = async (providerId) => {
    clearFieldErrors()
    setError('')
    setLoading(providerId)
    const provider = providers.find(p => p.id === providerId)

    try {
      const providerToken = await provider.signIn({ apiSource })

      const loginResponse = await acceptInviteAndLogin({ apiSource, token, providerToken })

      if (loginResponse.authenticated) {
        if (loginResponse.user.permissions.studio) {
          await applyLoginResponse(loginResponse, { preserveSelectedClient: false })
        } else {
          setStage("scan")
        }
      } else {
        setError(loginResponse.message)
      }
    } catch (err) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(null)
    }
  }

  const handlePasswordRegistration = async () => {
    const isPasswordValid = validatePasswordField(password, { requireStrength: true, score: passwordScore })
    if (!isPasswordValid) {
      return
    }
    setError('')
    setLoading('credentials')

    try {
      const loginResponse = await acceptInviteAndLogin({ apiSource,token, password })

      if (loginResponse.user?.permissions?.studio) {
        await applyLoginResponse(loginResponse, { preserveSelectedClient: false })
      } else {
        setStage("scan")
      }
    } catch (err) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(null)
    }
  }


  if (stage === "verifying") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Verifying invite </Typography>
      Please wait a moment whilst we verify your invitation...
      <div className='flex flex-row justify-around'>
        <CircularProgress />
      </div>
    </div>
  )

  if (stage === "form") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">

      <Typography variant='h5'>You have been invited to join {clientName}'s team</Typography>

      {error && <div className='text-red-600 dark:text-rose-400 text-xl text-center'>{error}</div>}

      {/* OAuth Buttons */}
      <div className='flex flex-col gap-3'>
        <Button
          variant='outlined'
          disabled={loading === 'google'}
          onClick={() => handleProviderRegistration('google')}
        >
          <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
            {loading === 'google' ? <CircularProgress size={20} /> :
              <>
                <img src={GoogleIcon} />
                <div>Accept with Google</div>
              </>
            }
          </div>
        </Button>

        <Button
          variant='outlined'
          disabled={loading === 'apple'}
          onClick={() => handleProviderRegistration('apple')}
        >
          <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
            {loading === 'apple' ? <CircularProgress size={20} /> :
              <>
                <img src={themeMode === "dark" ? AppleDarkIcon : AppleLightIcon} />
                <div>Accept with Apple</div>
              </>
            }
          </div>
        </Button>

        <Button
          variant='outlined'
          disabled={loading === 'linkedin'}
          onClick={() => handleProviderRegistration('linkedin')}
        >
          <div className='flex flex-row w-full ml-10 gap-5 justify-start'>
            {loading === 'linkedin' ? <CircularProgress size={20} /> :
              <>
                <img src={LinkedInIcon} />
                <div>Accept with LinkedIn</div>
              </>
            }
          </div>
        </Button>
      </div>

      <Divider>or</Divider>

      <Typography variant='h5'>Choose a password</Typography>
      <TextField
        label='Password'
        type='password'
        value={password}
        onChange={e => handlePasswordInputChange(e.target.value)}
        onBlur={() => validatePasswordField(password, { requireStrength: true })}
        fullWidth
        size='small'
        error={Boolean(passwordError)}
        helperText={passwordError}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <div className='opacity-60 text-sm mt-5'>Password strength (good or strong required)</div>
      <PasswordStrengthBar password={password} onChangeScore={handlePasswordScoreChange} />

      <Button onClick={handlePasswordRegistration} variant='contained'>Accept invitation</Button>
    </div>
  )

  if (stage === "rejected") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Unable to verify invitation</Typography>
      Your invitation may have expired. Please ask the accout holder to send a new invitation to you.
    </div>
  )

  if (stage === "scan") return (
    <div className="flex flex-col gap-5 p-10 w-[400px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
      <Typography variant='h5'>Account confirmed</Typography>
      Your account has been created, however you have not been assigned access to Studio. You can use your account to sign into the <a href="https://www.smartix.uk/scan/apps/" className="link">Smartix Scan app</a>.
    </div> 
  )
}

export default PasswordResetForm
